import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { PageBody } from '@abyss/web/ui/PageBody';
import { Router } from '@abyss/web/ui/Router';
import { FAQPage } from 'parcels/src/frontends/StandaloneFAQPage/FAQPage';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { AllPatientReviews } from '../../../common/AllPatientReviews/AllPatientReviews';
import { CardWrapper } from '../../../common/CardWrapper';
import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { ProviderLocationMap } from '../../../common/ProviderLocationMap';
import { getFeatureFlags } from '../../../common/Utils';
import { Page404 } from '../../../errors/Page404';
import { Page408 } from '../../../errors/Page408';
import { Page4XX } from '../../../errors/Page4XX';
import { Page500 } from '../../../errors/Page500';
import { useCoverageType } from '../../../hooks/useCoverageType';
import { useGetRecommendationPreferencesStatus } from '../../../hooks/useGetRecommendationPreferencesStatus';
import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useLagoon } from '../../../hooks/useLagoon';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import { getCurrentMember, getPolicyNumber } from '../../../utils/user.utils';
import { CareTeamView } from './CareTeamView';
import { ChoosePCPLocation, ChoosePCPSuccess } from './ChoosePCP';
import { FacilityDetails } from './FacilityDetails/FacilityDetails';
import { Footer } from './Footer';
import { GroupDetails } from './GroupDetails';
import { NullResultPageForSpecialty } from './NullSpecialtyPage';
import { ProviderDetailsView } from './ProviderDetailsView';
import { PSXHome } from './PSXHome';
import { SearchResultsV2 } from './SearchResults';
import { ViewAll } from './ViewAll';
import { VirtualProviderGroupDetails } from './VirtualProviderGroupDetails';

export const Routes = () => {
  const suppressionFeatureLagoonData = useLagoon('feature-supression')();
  const includeLagoonData = useLagoon('feature-inclusion')();
  const [isSuperUser, setIsSuperUser] = useState(
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false
  );
  const [hasDelayPassed, setHasDelayPassed] = useState(false);
  const featureFlags: [{ key: string; active: boolean }] = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  )();

  const [enableSmartChoiceIntroPopup] = getFeatureFlags(featureFlags, [
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SMART_CHOICE_INTRO_POPUP,
  ]);

  const setRecommendationPreferences = useStore(
    StoreKeys.SET_RECOMMENDATION_PREFERENCES
  );
  const hsidUuid = useHsidUuid();
  const isSmartChoice = useSmartChoice(true);
  const [preferencesData, getPreferenceData] =
    useGetRecommendationPreferencesStatus({});

  const { navigate } = useRouter();
  const isGEHAV2 = shouldUseGehaV2Styling();
  const setUIFeatureSuppressionState = useStore(
    StoreKeys.SET_UI_FEATURE_SUPPRESSION
  );
  const setUIFeatureInclusionState = useStore(
    StoreKeys.SET_UI_FEATURE_INCLUSION
  );
  const coverageType = useCoverageType();
  const currentMember = getCurrentMember();

  const handleHomeClick = () => {
    navigate('/?chipValue=All');
  };

  window.addEventListener('find-care-link-clicked', handleHomeClick);

  const memberPolicy =
    getPolicyNumber(currentMember, coverageType) ?? currentMember?.policyNumber;

  const suppressionLagoonData = useMemo(
    () => suppressionFeatureLagoonData,
    [JSON.stringify(suppressionFeatureLagoonData)]
  );

  useEffect(() => {
    setUIFeatureSuppressionState({ suppressionLagoonData, memberPolicy });
  }, [suppressionLagoonData, memberPolicy]);

  useEffect(() => {
    setUIFeatureInclusionState({ includeLagoonData, memberPolicy });
  }, [includeLagoonData, memberPolicy]);

  useEffect(() => {
    let timeout;
    const shouldFetchPreferences =
      isSmartChoice && typeof getPreferenceData === 'function';
    if (shouldFetchPreferences) {
      getPreferenceData({
        variables: { ...(hsidUuid && { userId: hsidUuid }), isSuperUser },
      });
      timeout = setTimeout(() => {
        setHasDelayPassed(true);
      }, 2000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isSmartChoice, isSuperUser]);

  useEffect(() => {
    const handleEventChange = async () => {
      const tempIsSuperUser =
        storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;
      setIsSuperUser(tempIsSuperUser);
    };
    window.addEventListener('updateSuperUser', handleEventChange);
    return () => {
      window.removeEventListener('updateSuperUser', handleEventChange);
    };
  }, []);

  useEffect(() => {
    if (
      isSmartChoice &&
      preferencesData?.data?.recommendationPreferencesStatus &&
      hasDelayPassed
    ) {
      const { hasDismissedPopover, hasDismissedCoachMarkWeb } =
        preferencesData?.data?.recommendationPreferencesStatus;
      if (enableSmartChoiceIntroPopup) {
        if (
          hasDismissedPopover !== undefined &&
          hasDismissedPopover !== null &&
          (sessionStorage.getItem('hasDismissedPopover') === null ||
            isSuperUser)
        ) {
          sessionStorage.setItem('hasDismissedPopover', hasDismissedPopover);
        } else if (
          hasDismissedPopover !== undefined &&
          hasDismissedPopover === null &&
          (sessionStorage.getItem('hasDismissedPopover') === null ||
            isSuperUser)
        ) {
          sessionStorage.setItem('hasDismissedPopover', 'n');
        }
        if (
          hasDismissedCoachMarkWeb !== undefined &&
          (sessionStorage.getItem('hasDismissedCoachMarkWeb') === null ||
            isSuperUser)
        ) {
          sessionStorage.setItem(
            'hasDismissedCoachMarkWeb',
            hasDismissedCoachMarkWeb
          );
        }
      }
      setRecommendationPreferences(
        preferencesData?.data?.recommendationPreferencesStatus
      );
    }
  }, [preferencesData, isSuperUser, hasDelayPassed]);
  return (
    <PageBody
      css={{
        'abyss-page-body-container': {
          backgroundColor: '$pageBodyBgColor',
          maxWidth: '100%',
          padding: 0,
        },
      }}
      id="main-content"
      tabIndex={-1}
    >
      <Suspense fallback={<div>LOADING...</div>}>
        <Router.Routes>
          <Router.Route element={<PSXHome />} path="/" />
          <Router.Route element={<PSXHome />} path="/:token" />
          <Router.Route element={<SearchResultsV2 />} path="/results" />
          <Router.Route element={<SearchResultsV2 />} path="/results/:token" />
          <Router.Route
            element={<NullResultPageForSpecialty />}
            path="/no-specialty-results"
          />
          <Router.Route
            element={<NullResultPageForSpecialty />}
            path="/no-specialty-results/:token"
          />
          {/* search results page with list view */}
          <Router.Route element={<ViewAll />} path="/results/viewall" />
          <Router.Route element={<ViewAll />} path="/results/viewall/:token" />
          {/* search results page with map view */}
          <Router.Route element={<ViewAll />} path="/results/mapview" />
          <Router.Route element={<ViewAll />} path="/results/mapview/:token" />
          <Router.Route
            element={<ProviderDetailsView />}
            path="/provider-details"
          />
          <Router.Route
            element={<ProviderDetailsView />}
            path="/provider-details/:token"
          />
          <Router.Route
            element={<AllPatientReviews />}
            path="/provider-details/reviews"
          />
          <Router.Route
            element={<AllPatientReviews />}
            path="/provider-details/reviews/:token"
          />
          <Router.Route
            element={<FacilityDetails />}
            path="/facility-details"
          />
          <Router.Route
            element={<FacilityDetails />}
            path="/facility-details/:token"
          />
          <Router.Route element={<GroupDetails />} path="/group-details" />
          <Router.Route
            element={<GroupDetails />}
            path="/group-details/:token"
          />
          <Router.Route
            element={<VirtualProviderGroupDetails />}
            path="/virtual-group-details"
          />
          <Router.Route
            element={<VirtualProviderGroupDetails />}
            path="/virtual-group-details/:token"
          />
          <Router.Route
            element={<ChoosePCPLocation />}
            path="/choose-pcp/locations"
          />
          <Router.Route
            element={<ChoosePCPLocation />}
            path="/choose-pcp/locations/:token"
          />
          <Router.Route
            element={<ChoosePCPSuccess />}
            path="/choose-pcp/success"
          />
          <Router.Route
            element={<ChoosePCPSuccess />}
            path="/choose-pcp/success/:token"
          />
          <Router.Route element={<Page500 />} path="/500" />
          <Router.Route element={<Page408 />} path="/408" />
          <Router.Route element={<Page404 />} path="/404" />
          <Router.Route element={<Page4XX />} path="/4xx" />
          <Router.Route element={<Page500 />} path="/500/:token" />
          <Router.Route element={<Page408 />} path="/408/:token" />
          <Router.Route element={<Page404 />} path="/404/:token" />
          <Router.Route element={<Page4XX />} path="/4xx/:token" />
          <Router.Route element={<FAQPage />} path="/faq" />
          <Router.Route element={<CareTeamView />} path="/care-team" />
          <Router.Route
            element={<ProviderLocationMap />}
            path="/provider-location-map"
          />
        </Router.Routes>
      </Suspense>
      <CardWrapper
        backgroundColor={isGEHAV2 ? '#FAFAFA' : '$infoFooterBackground'}
      >
        <Footer />
      </CardWrapper>
    </PageBody>
  );
};
